import { lazy } from "react";
import seoData from "../data/seo.json";
import * as WebdevelopmentPageData from "../pages/Services/WebDevelopment/WebdevelopmentPageData";
import * as MobileAppDevelopmentPageData from "../pages/Services/MobileAppDevelopment/MobileAppDevelopmentPageData";
import * as DigitalMarketingPageData from "../pages/Services/DigitalMarketing/DigitalMarketingPageData";
import * as BrandingPageData from "../pages/Services/Branding/BrandingPageData";
import PrivateRoute from "../hooks/PrivateRoute";

// Lazy load components
const Home = lazy(() => import("../pages/Home"));
const Aboutus = lazy(() => import("../pages/Aboutus"));
const Contactus = lazy(() => import("../pages/Contactus"));
const Quotation = lazy(() => import("../pages/Quotation"));
const Services = lazy(() => import("../pages/Services/Services"));
const Blog = lazy(() => import("../pages/Blogs/Blog"));
const BlogFullPost = lazy(() => import("../pages/Blogs/BlogFullPost"));
const FAQ = lazy(() => import("../pages/Faqs"));
const Clients = lazy(() => import("../pages/Clients"));
const Products = lazy(() => import("../pages/Products"));
const Works = lazy(() => import("../pages/Works/Works"));
const WorksSubPages = lazy(() => import("../pages/Works/WorksSubPages"));
const FuturisticProjects = lazy(() => import("../pages/futuristicprojects"));
const Careers = lazy(() => import("../pages/Careers"));
const Visakhapatnam = lazy(() => import("../pages/LocationPages/Visakhapatnam"));

// Legal components
const PrivacyPolicy = lazy(() => import("../components/Documents/PrivacyPolicy"));
const TermsnConditions = lazy(() => import("../components/Documents/TermsnConditions"));
const ReturnPolicy = lazy(() => import("../components/Documents/ReturnsPolicy"));
const ShippingandDelivery = lazy(() => import("../components/Documents/ShippingandDelivery"));
const Disclaimer = lazy(() => import("../components/Documents/Disclaimer"));
const Error404 = lazy(() => import("../components/Tools/Error404"));

// Auth components
const Login = lazy(() => import("../pages/auth/login"));
const Dashboard = lazy(() => import("../pages/auth/dashboard"));
const AdminContactus = lazy(() => import("../pages/auth/Admin/AdminContactus.jsx"));
const GoogleAnalytics = lazy(() => import("../pages/auth/Admin/GoogleAnalytics.jsx"));
const BlogsAdmin = lazy(() => import("../pages/auth/Admin/BlogsAdmin.jsx"));
const Profile = lazy(() => import("../pages/auth/Profile"));
const QuickPay = lazy(() => import("../pages/Renewals/QuickPay"));
const DomainRenewal = lazy(() => import("../pages/Renewals/DomainRenewal.js"));

// Service Subpages
const WebDevelopment = lazy(() => import("../pages/Services/WebDevelopment/Webdevelopment"));
const DigitalMarketing = lazy(() => import("../pages/Services/DigitalMarketing/DigitalMarketing"));
const Branding = lazy(() => import("../pages/Services/Branding/Branding"));
const BrandingSubPages = lazy(() => import("../pages/Services/Branding/BrandingSubPages"));
const MobileAppDevelopment = lazy(() => import("../pages/Services/MobileAppDevelopment/MobileAppDevelopment"));
const WebdevelopmentSubPages = lazy(() => import("../pages/Services/WebDevelopment/WebdevelopmentSubPages"));
const DigitalMarketingSubPages = lazy(() => import("../pages/Services/DigitalMarketing/DigitalMarketingSubPages"));
const MobileAppDevelopmentSubPages = lazy(() => import("../pages/Services/MobileAppDevelopment/MobileAppDevelopmentSubPages"));

const routes = [
  { path: "/", element: <Home /> },
  { path: "/*", element: <Error404 /> },
  { path: "/auth/login", element: <Login /> },
  { path: "/auth/dashboard", element: <PrivateRoute><Dashboard /></PrivateRoute> },
  { path: "/auth/contactus", element: <PrivateRoute><AdminContactus /></PrivateRoute> },
  { path: "/auth/google-analytics", element: <PrivateRoute><GoogleAnalytics /></PrivateRoute> },
  { path: "/auth/dashboard/profile", element: <PrivateRoute><Profile /></PrivateRoute> },
  { path: "/auth/dashboard/blogs", element: <PrivateRoute><BlogsAdmin /></PrivateRoute> },
  { path: "/blogs", element: <Blog /> },
  { path: "/blogs/:slug", element: <BlogFullPost /> },
  { path: "/aboutus", element: <Aboutus /> },
  { path: "/quotation", element: <Quotation /> },
  { path: "/clients", element: <Clients /> },
  { path: "/products", element: <Products /> },
  { path: "/contactus", element: <Contactus /> },
  { path: "/custom-payment", element: <QuickPay /> },
  { path: "/domain-renewal", element: <DomainRenewal /> },
  { path: "/Legal/privacy-policy", element: <PrivacyPolicy /> },
  { path: "/Legal/terms-and-conditions", element: <TermsnConditions /> },
  { path: "/Legal/return-policy", element: <ReturnPolicy /> },
  { path: "/Legal/shipping-and-delivery", element: <ShippingandDelivery /> },
  { path: "/Legal/disclaimer", element: <Disclaimer /> },
  { path: "/services", element: <Services /> },
  { path: "/futuristic-projects", element: <FuturisticProjects /> },
  { path: "/frequently-asked-questions", element: <FAQ /> },
  { path: "/careers", element: <Careers /> },
  { path: "/works", element: <Works /> },
  { path: "/works/:projectSlug", element: <WorksSubPages /> },
  { path: "/locations/visakhapatnam", element: <Visakhapatnam /> },
  { path: "/services/web-development-company", element: <WebDevelopment /> },
  {
    path: "/services/web-development-company/build-business-website",
    element: <WebdevelopmentSubPages pageData={WebdevelopmentPageData.BusinessWebsitePage} seoData={seoData} />,
  },
  {
    path: "/services/web-development-company/build-ecommerce-website",
    element: <WebdevelopmentSubPages pageData={WebdevelopmentPageData.EcommercePageDetails} seoData={seoData} />,
  },
  {
    path: "/services/web-development-company/website-rebuild",
    element: <WebdevelopmentSubPages pageData={WebdevelopmentPageData.WebRevapPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/web-development-company/control-center-for-business",
    element: <WebdevelopmentSubPages pageData={WebdevelopmentPageData.CRMERPPageDetails} seoData={seoData} />,
  },
  { path: "/services/mobile-app-development-company", element: <MobileAppDevelopment /> },
  {
    path: "/services/mobile-app-development-company/mobile-app-design",
    element: <MobileAppDevelopmentSubPages pageData={MobileAppDevelopmentPageData.MobileAppDesignDetails} seoData={seoData} />,
  },
  {
    path: "/services/mobile-app-development-company/ios-application-development",
    element: <MobileAppDevelopmentSubPages pageData={MobileAppDevelopmentPageData.IOSAppDevelopmentDetails} seoData={seoData} />,
  },
  {
    path: "/services/mobile-app-development-company/android-app-development",
    element: <MobileAppDevelopmentSubPages pageData={MobileAppDevelopmentPageData.AndroidAppDevelopmentDetails} seoData={seoData} />,
  },
  {
    path: "/services/mobile-app-development-company/cross-platform-app-development",
    element: <MobileAppDevelopmentSubPages pageData={MobileAppDevelopmentPageData.CrossPlatformAppDevelopmentDetails} seoData={seoData} />,
  },
  {
    path: "/services/mobile-app-development-company/app-prototyping",
    element: <MobileAppDevelopmentSubPages pageData={MobileAppDevelopmentPageData.AppPrototypingDetails} seoData={seoData} />,
  },
  {
    path: "/services/mobile-app-development-company/app-testing-and-quality-assurance",
    element: <MobileAppDevelopmentSubPages pageData={MobileAppDevelopmentPageData.AppTestingandQualityAssuranceDetails} seoData={seoData} />,
  },
  {
    path: "/services/mobile-app-development-company/app-maintenance-and-support",
    element: <MobileAppDevelopmentSubPages pageData={MobileAppDevelopmentPageData.AppMaintenanceandSupportDetails} seoData={seoData} />,
  },
  {
    path: "/services/mobile-app-development-company/app-security",
    element: <MobileAppDevelopmentSubPages pageData={MobileAppDevelopmentPageData.AppSecurityDetails} seoData={seoData} />,
  },
  {
    path: "/services/mobile-app-development-company/app-monetization-strategy",
    element: <MobileAppDevelopmentSubPages pageData={MobileAppDevelopmentPageData.AppMonetizationStrategyDetails} seoData={seoData} />,
  },
  {
    path: "/services/mobile-app-development-company/app-store-optimization",
    element: <MobileAppDevelopmentSubPages pageData={MobileAppDevelopmentPageData.AppStoreOptimizationDetails} seoData={seoData} />,
  },
  {
    path: "/services/mobile-app-development-company/ecommerce-app-development",
    element: <MobileAppDevelopmentSubPages pageData={MobileAppDevelopmentPageData.EcommerceAppDevelopmentDetails} seoData={seoData} />,
  },
  { path: "/services/digital-marketing-company", element: <DigitalMarketing /> },
  { path: "/services/branding-company", element: <Branding /> },
  {
    path: "/services/digital-marketing-company/search-engine-optimization",
    element: <DigitalMarketingSubPages pageData={DigitalMarketingPageData.SearchEngineOptimizationPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/digital-marketing-company/content-marketing",
    element: <DigitalMarketingSubPages pageData={DigitalMarketingPageData.ContentMarketingPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/digital-marketing-company/social-media-marketing",
    element: <DigitalMarketingSubPages pageData={DigitalMarketingPageData.SocialMediaMarketingPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/digital-marketing-company/email-marketing",
    element: <DigitalMarketingSubPages pageData={DigitalMarketingPageData.EmailMarketingPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/digital-marketing-company/pay-per-click-advertising",
    element: <DigitalMarketingSubPages pageData={DigitalMarketingPageData.PayPerClickAdvertisingPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/digital-marketing-company/affiliate-marketing",
    element: <DigitalMarketingSubPages pageData={DigitalMarketingPageData.AffiliateMarketingPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/digital-marketing-company/analytics-and-data-analysis",
    element: <DigitalMarketingSubPages pageData={DigitalMarketingPageData.AnalyticsAndDataAnalysisPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/digital-marketing-company/conversion-rate-optimization",
    element: <DigitalMarketingSubPages pageData={DigitalMarketingPageData.ConversionRateOptimizationPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/digital-marketing-company/mobile-marketing",
    element: <DigitalMarketingSubPages pageData={DigitalMarketingPageData.MobileMarketingPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/digital-marketing-company/video-marketing",
    element: <DigitalMarketingSubPages pageData={DigitalMarketingPageData.VideoMarketingPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/digital-marketing-company/local-seo-and-marketing",
    element: <DigitalMarketingSubPages pageData={DigitalMarketingPageData.LocalSeoAndMarketingPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/digital-marketing-company/ecommerce-marketing",
    element: <DigitalMarketingSubPages pageData={DigitalMarketingPageData.EcommerceMarketingPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/digital-marketing-company/marketing-automation",
    element: <DigitalMarketingSubPages pageData={DigitalMarketingPageData.MarketingAutomationPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/digital-marketing-company/remarketing-retargeting",
    element: <DigitalMarketingSubPages pageData={DigitalMarketingPageData.RemarketingRetargetingPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/digital-marketing-company/influencer-marketing",
    element: <DigitalMarketingSubPages pageData={DigitalMarketingPageData.InfluencerMarketingPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/branding-company/brand-identity-design-exclusive",
    element: <BrandingSubPages pageData={BrandingPageData.BrandIdentityDesignExclusivePageDetails} seoData={seoData} />,
  },
  {
    path: "/services/branding-company/brand-logo-design",
    element: <BrandingSubPages pageData={BrandingPageData.BrandLogoDesignPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/branding-company/brand-guidelines",
    element: <BrandingSubPages pageData={BrandingPageData.BrandGuidelinesPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/branding-company/brand-positioning",
    element: <BrandingSubPages pageData={BrandingPageData.BrandPositioningPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/branding-company/brand-storytelling",
    element: <BrandingSubPages pageData={BrandingPageData.BrandStorytellingPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/branding-company/brand-naming",
    element: <BrandingSubPages pageData={BrandingPageData.BrandNamingPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/branding-company/visual-identity-design",
    element: <BrandingSubPages pageData={BrandingPageData.VisualIdentityDesignPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/branding-company/brand-messaging",
    element: <BrandingSubPages pageData={BrandingPageData.BrandMessagingPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/branding-company/brand-experience-design",
    element: <BrandingSubPages pageData={BrandingPageData.BrandExperienceDesignPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/branding-company/brand-awareness-campaigns",
    element: <BrandingSubPages pageData={BrandingPageData.BrandAwarenessCampaignsPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/branding-company/rebranding-services",
    element: <BrandingSubPages pageData={BrandingPageData.RebrandingServicesPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/branding-company/brand-research-and-analysis",
    element: <BrandingSubPages pageData={BrandingPageData.BrandResearchAndAnalysisPageDetails} seoData={seoData} />,
  },
  {
    path: "/services/branding-company/brand-tracking-and-performance-evaluation",
    element: <BrandingSubPages pageData={BrandingPageData.BrandTrackingAndPerformanceEvaluationPageDetails} seoData={seoData} />,
  },
];

export default routes;
